import React, { useMemo } from 'react';
import { Card, ProgressBar, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '../../common/Flex';

const BudgetVsTarget = ({ orders, assignments, clients }) => {
  const targetData = useMemo(() => {
    // Simular objetivos basados en datos históricos
    const totalBudget = orders.reduce((sum, order) => sum + (parseFloat(order.netInvestment) || 0), 0);
    const totalSpent = assignments.reduce((sum, assignment) => {
      let amount = parseFloat(assignment.montoValor) || 0;
      if (assignment.amountType === 'percentage') {
        const order = orders.find(o => o.id === assignment.insertionOrderId);
        if (order && order.netInvestment) {
          amount = (amount * parseFloat(order.netInvestment)) / 100;
        }
      }
      return sum + amount;
    }, 0);

    // Objetivos simulados (en un proyecto real vendrían de la base de datos)
    const targets = {
      utilizationTarget: 85, // 85% de utilización objetivo
      budgetTarget: totalBudget * 1.2, // 20% más que el presupuesto actual
      ordersTarget: Math.ceil(orders.length * 1.15), // 15% más órdenes
      clientsTarget: Math.ceil(clients.length * 1.1) // 10% más clientes
    };

    const currentUtilization = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;

    return {
      totalBudget,
      totalSpent,
      currentUtilization,
      targets,
      performance: {
        utilization: (currentUtilization / targets.utilizationTarget) * 100,
        budget: (totalBudget / targets.budgetTarget) * 100,
        orders: (orders.length / targets.ordersTarget) * 100,
        clients: (clients.length / targets.clientsTarget) * 100
      }
    };
  }, [orders, assignments, clients]);

  const getPerformanceColor = (percentage) => {
    if (percentage >= 100) return 'success';
    if (percentage >= 80) return 'warning';
    return 'danger';
  };

  const metrics = [
    {
      label: 'Utilización de Presupuesto',
      current: targetData.currentUtilization,
      target: targetData.targets.utilizationTarget,
      performance: targetData.performance.utilization,
      icon: 'chart-pie',
      suffix: '%'
    },
    {
      label: 'Meta de Presupuesto',
      current: targetData.totalBudget / 1000,
      target: targetData.targets.budgetTarget / 1000,
      performance: targetData.performance.budget,
      icon: 'dollar-sign',
      suffix: 'K'
    },
    {
      label: 'Órdenes Objetivo',
      current: orders.length,
      target: targetData.targets.ordersTarget,
      performance: targetData.performance.orders,
      icon: 'file-alt',
      suffix: ''
    },
    {
      label: 'Clientes Objetivo',
      current: clients.length,
      target: targetData.targets.clientsTarget,
      performance: targetData.performance.clients,
      icon: 'users',
      suffix: ''
    }
  ];

  return (
    <Card className="h-100">
      <Card.Header>
        <Flex justifyContent="between" alignItems="center">
          <h6 className="mb-0">Presupuesto vs Objetivos</h6>
                      <FontAwesomeIcon icon="bullseye" className="text-success" />
        </Flex>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          {metrics.map((metric, index) => (
            <Col md={6} key={index}>
              <div className="border rounded p-3 h-100">
                <Flex alignItems="center" className="mb-2">
                  <FontAwesomeIcon icon={metric.icon} className="text-muted me-2" />
                  <h6 className="mb-0 fs-11">{metric.label}</h6>
                </Flex>
                
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fs-5 fw-bold">
                    {metric.current.toFixed(metric.suffix === '%' ? 1 : 0)}{metric.suffix}
                  </span>
                  <span className="fs-11 text-muted">
                    Meta: {metric.target.toFixed(metric.suffix === '%' ? 1 : 0)}{metric.suffix}
                  </span>
                </div>
                
                <ProgressBar 
                  now={Math.min(100, metric.performance)} 
                  variant={getPerformanceColor(metric.performance)}
                  className="mb-2"
                  style={{ height: '8px' }}
                />
                
                <div className="d-flex justify-content-between fs-11">
                  <span className={`text-${getPerformanceColor(metric.performance)}`}>
                    {metric.performance.toFixed(1)}% completado
                  </span>
                  {metric.performance >= 100 ? (
                    <span className="text-success">
                      <FontAwesomeIcon icon="check-circle" className="me-1" />
                      Objetivo alcanzado
                    </span>
                  ) : (
                    <span className="text-muted">
                      {(metric.target - metric.current).toFixed(0)}{metric.suffix} restante
                    </span>
                  )}
                </div>
              </div>
            </Col>
          ))}
        </Row>
        
        <div className="mt-4 p-3 bg-light rounded">
          <h6 className="mb-2">Performance General</h6>
          <div className="d-flex align-items-center">
            <div className="flex-1">
              <ProgressBar className="mb-1" style={{ height: '10px' }}>
                <ProgressBar 
                  variant="success" 
                  now={Math.min(25, targetData.performance.utilization / 4)} 
                  key={1} 
                />
                <ProgressBar 
                  variant="info" 
                  now={Math.min(25, targetData.performance.budget / 4)} 
                  key={2} 
                />
                <ProgressBar 
                  variant="warning" 
                  now={Math.min(25, targetData.performance.orders / 4)} 
                  key={3} 
                />
                <ProgressBar 
                  variant="danger" 
                  now={Math.min(25, targetData.performance.clients / 4)} 
                  key={4} 
                />
              </ProgressBar>
              <small className="text-muted">
                Performance promedio: {((targetData.performance.utilization + targetData.performance.budget + targetData.performance.orders + targetData.performance.clients) / 4).toFixed(1)}%
              </small>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default BudgetVsTarget; 